'use client'

import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { useEffect, useId, useRef, useState, useSyncExternalStore } from 'react'
import { FaApple, FaGooglePlay } from 'react-icons/fa'
import AppStoreLink from './AppStoreLink'
import GooglePlayLink from './GooglePlayLink'
import StoreBadges from './StoreBadges'
import { track } from '@/utils/analytics'
import { classifyInstallPlatform, type InstallPlatform } from '@/utils/appStoreTracking'

/**
 * Navigation install control, labeled "Get the app".
 *
 * - iPhone, iPad and Android: one tap goes straight to that device's store
 *   listing. No menu, no choosing a badge.
 * - Desktop: nobody installs a phone app here, so the button opens a panel
 *   with a scan-to-install QR code and both official store badges.
 *
 * The QR encodes https://microplasticsapp.com/get?s=nav_qr. /get reads the
 * phone's user agent and redirects to the App Store or Google Play with
 * `nav_qr` as the campaign token, so one code serves both platforms and its
 * installs show up as their own campaign. Regenerate public/qr/get-app.svg with:
 *   npx qrcode@1.5.4 -t svg -e M -m 2 -d 152319 -l ffffff \
 *     -o public/qr/get-app.svg "https://microplasticsapp.com/get?s=nav_qr"
 *
 * The server always renders the desktop button (the site is static); phones
 * swap to the direct store link as soon as React hydrates.
 */
const QR_SRC = '/qr/get-app.svg'
const LABEL = 'Get the app'

const subscribe = () => () => {}
const getPlatform = (): InstallPlatform =>
  classifyInstallPlatform(navigator.userAgent, navigator.maxTouchPoints)
const getServerPlatform = (): InstallPlatform => 'desktop'

export default function GetAppMenu({
  onDark = false,
  compact = false,
  source = 'nav',
  className = '',
}: {
  onDark?: boolean
  /** Smaller pill for the mobile header bar. */
  compact?: boolean
  /** Attribution label for the direct store tap on phones. */
  source?: string
  className?: string
}) {
  const platform = useSyncExternalStore(subscribe, getPlatform, getServerPlatform)
  const [open, setOpen] = useState(false)
  const reduce = useReducedMotion()
  const panelId = useId()
  const headingId = useId()
  const rootRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false)
        buttonRef.current?.focus()
      }
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const pillClass = `inline-flex items-center whitespace-nowrap rounded-control font-semibold transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 pressable ${
    compact ? 'px-3 py-1.5 min-h-[40px] text-[0.8125rem] gap-1.5' : 'gap-2 px-4 py-2 min-h-[40px] text-sm'
  } ${
    onDark
      ? 'bg-cream text-ink hover:bg-white focus-visible:ring-goldLight focus-visible:ring-offset-deepForest'
      : 'bg-ink text-cream hover:bg-ink/90 focus-visible:ring-forest focus-visible:ring-offset-cream'
  }`
  const directParams = { cta_copy: LABEL, cta_component: 'nav_get_app_button' }

  if (platform === 'ios') {
    return (
      <div className={`shrink-0 ${className}`}>
        <AppStoreLink source={source} ctaPosition="nav_button" trackingParams={directParams} className={pillClass}>
          <FaApple className={`h-4 w-4 -mt-px ${compact ? 'hidden min-[390px]:block' : ''}`} aria-hidden="true" />
          {LABEL}
        </AppStoreLink>
      </div>
    )
  }
  if (platform === 'android') {
    return (
      <div className={`shrink-0 ${className}`}>
        <GooglePlayLink source={source} ctaPosition="nav_button" trackingParams={directParams} className={pillClass}>
          <FaGooglePlay className={`h-3.5 w-3.5 ${compact ? 'hidden min-[390px]:block' : ''}`} aria-hidden="true" />
          {LABEL}
        </GooglePlayLink>
      </div>
    )
  }

  return (
    <div ref={rootRef} className={`relative shrink-0 ${className}`}>
      <button
        ref={buttonRef}
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        aria-haspopup="dialog"
        onClick={() => {
          setOpen((v) => !v)
          // Surface and action ids predate the relabel; kept so the open rate
          // stays one continuous series in GA4 and PostHog.
          if (!open) track('site_button_click', { cta_surface: 'nav_download_menu', action_id: 'open_download_menu' })
        }}
        className={pillClass}
      >
        {/* Phone outline: says "this is for your phone" before the panel opens. */}
        <svg viewBox="0 0 20 20" className={`h-4 w-4 ${compact ? 'hidden min-[390px]:block' : ''}`} fill="none" stroke="currentColor" strokeWidth={1.7} aria-hidden="true">
          <rect x="5.5" y="2.5" width="9" height="15" rx="2" />
          <path strokeLinecap="round" d="M8.75 14.75h2.5" />
        </svg>
        {LABEL}
        <svg viewBox="0 0 20 20" className={`h-3.5 w-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} fill="currentColor" aria-hidden="true">
          <path d="M5.2 7.6a.75.75 0 0 1 1.06.04L10 11.6l3.74-3.96a.75.75 0 1 1 1.1 1.02l-4.3 4.55a.75.75 0 0 1-1.1 0l-4.3-4.55a.75.75 0 0 1 .06-1.06Z" />
        </svg>
      </button>

      <AnimatePresence>
        {open ? (
          <motion.div
            id={panelId}
            role="dialog"
            aria-labelledby={headingId}
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: -6, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, y: -4, scale: 0.98 }}
            transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
            className="absolute right-0 top-full z-50 mt-3 w-[312px] max-w-[calc(100vw-2rem)] origin-top-right rounded-panel border border-ink/10 bg-white p-5 text-center shadow-[0_24px_60px_-20px_rgba(21,35,25,0.35)]"
          >
            <span aria-hidden="true" className="absolute -top-1.5 right-10 h-3 w-3 rotate-45 border-l border-t border-ink/10 bg-white" />
            <p id={headingId} className="font-display text-lg font-bold leading-tight text-ink">
              Get the app on your phone
            </p>
            <p className="mx-auto mt-1.5 max-w-[15rem] text-sm leading-snug text-gray-600">
              Point your phone&rsquo;s camera at the code. It opens the App Store or Google Play.
            </p>
            {/* eslint-disable-next-line @next/next/no-img-element -- static SVG; next/image adds nothing for a 1.5 KB vector */}
            <img
              src={QR_SRC}
              alt="QR code that opens MicroPlastics in the App Store on iPhone or Google Play on Android"
              width={160}
              height={160}
              className="mx-auto mt-4 h-40 w-40 rounded-xl border border-ink/10 bg-white p-1.5"
            />
            <div className="my-4 flex items-center gap-3 text-[0.6875rem] font-semibold uppercase tracking-[0.12em] text-gray-500">
              <span aria-hidden="true" className="h-px flex-1 bg-ink/10" />
              or open a store
              <span aria-hidden="true" className="h-px flex-1 bg-ink/10" />
            </div>
            <div onClick={() => setOpen(false)}>
              <StoreBadges source="nav" ctaPosition="nav" size="sm" align="center" className="flex-nowrap gap-2" />
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  )
}
